import { BookOpen, Check, ChevronLeft, FileUp, Globe, Layers, Loader2, Plus, Save, Sparkles, Upload, X, Zap } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DialogFooter } from "../ui/dialog";
import CharacterEditor from "../CharacterEditor";
import SpeechToTextButton from "../SpeechToTextButton";
import { toast } from "sonner";
import axios from "axios";

const ManualBuilderMode = ({
  // Core form state
  formData, setFormData,
  step, setStep,
  complexityLevel, setComplexityLevel,
  characters, setCharacters,
  // Chapter planning
  chapterPlanMode, setChapterPlanMode,
  chapterSize, setChapterSize,
  planReviewOutline, setPlanReviewOutline,
  generatingPlan,
  userSituations, setUserSituations,
  manualChapters, setManualChapters,
  // Upload state
  uploadedStoryBible, setUploadedStoryBible,
  uploadedChaptersVerbatim,
  smartUploadMode, setSmartUploadMode,
  uploadingStoryBible, setUploadingStoryBible,
  uploadingOutline, setUploadingOutline,
  uploadConflicts, setUploadConflicts,
  conflictResolutions, setConflictResolutions,
  resolvingConflicts, setResolvingConflicts,
  uploadActivityLog, setUploadActivityLog,
  hasUnsavedChanges, setHasUnsavedChanges,
  // Actions
  loading,
  genres,
  setMode,
  onGenerateChapterPlan: handleGenerateChapterPlan,
  onManualCreate: handleManualCreate,
  onPopulateFromUploads: populateFormFromUploads,
  // Extraction helpers
  extractAge, extractGender, extractField,
  // Utilities
  API,
  // Book options (for dropdowns)
  bookOptions = null,
}) => {
  return (
          <>
            {/* STEP 0: Choose Complexity Level */}
            {!complexityLevel && !smartUploadMode && (
              <div className="space-y-4">
                {/* SMART UPLOAD - Featured prominently */}
                <Card 
                  className="cursor-pointer border-2 border-purple-500 bg-purple-900/30 hover:bg-purple-900/50 transition-all"
                  onClick={() => setSmartUploadMode(true)}
                  data-testid="smart-upload-option"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center shrink-0">
                        <FileUp className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground flex items-center gap-2">
                          Upload Your Story Bible
                          <Badge className="bg-purple-600 text-white text-xs">Recommended</Badge>
                        </h3>
                        <p className="text-sm text-muted-foreground">Already have character profiles, chapter outlines, or world-building notes? Upload them and we'll auto-fill everything.</p>
                        <p className="text-xs text-purple-400 mt-1">PDF or TXT • Your words become the blueprint</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-700" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or start from scratch</span>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  How detailed do you want your story setup to be?
                </p>
                
                <div className="grid gap-3">
                  {/* Simple Mode */}
                  <Card 
                    className="cursor-pointer border-2 hover:border-green-400 hover:bg-green-50/50 transition-all"
                    onClick={() => { 
                      setComplexityLevel("simple"); 
                      setStep(1);
                      // Auto-fill form from uploaded data if available
                      populateFormFromUploads();
                    }}
                    data-testid="complexity-simple"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                          <Zap className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Quick Start</h3>
                          <p className="text-sm text-muted-foreground">Just the basics - title, genre, main character. Perfect for getting started fast.</p>
                          <p className="text-xs text-green-600 mt-1">~2 minutes</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Moderate Mode */}
                  <Card 
                    className="cursor-pointer border-2 hover:border-cyan-400 hover:bg-slate-800/50/50 transition-all"
                    onClick={() => { 
                      setComplexityLevel("moderate"); 
                      setStep(1);
                      // Auto-fill form from uploaded data if available
                      populateFormFromUploads();
                    }}
                    data-testid="complexity-moderate"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-800/60 flex items-center justify-center shrink-0">
                          <Layers className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Standard Setup</h3>
                          <p className="text-sm text-muted-foreground">Add characters, setting, era, and story arc. Good balance of detail and speed.</p>
                          <p className="text-xs text-cyan-400 mt-1">~5 minutes</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Detailed Mode */}
                  <Card 
                    className="cursor-pointer border-2 hover:border-indigo-400 hover:bg-indigo-50/50 transition-all"
                    onClick={() => { 
                      setComplexityLevel("detailed"); 
                      setStep(1);
                      // Auto-fill form from uploaded data if available
                      populateFormFromUploads();
                    }}
                    data-testid="complexity-detailed"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                          <Globe className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Full World-Builder</h3>
                          <p className="text-sm text-muted-foreground">Complete creative control - world type, technology, magic/supernatural, religion, society structure.</p>
                          <p className="text-xs text-indigo-600 mt-1">~10-15 minutes</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <Button variant="outline" onClick={() => setMode("choose")} className="w-full mt-4">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back to Options
                </Button>
              </div>
            )}
            
            {/* SMART UPLOAD MODE - Upload Your Story Bible */}
            {smartUploadMode && !uploadedStoryBible && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground text-lg flex items-center gap-2">
                      <FileUp className="w-5 h-5 text-purple-400" />
                      Upload Your Story Bible
                    </h3>
                    <p className="text-sm text-muted-foreground">Upload one or more files with your characters, world, chapters, etc.</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSmartUploadMode(false)}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                </div>
                
                <div className="border-2 border-dashed border-purple-500/50 rounded-lg p-8 text-center hover:border-purple-400 transition-colors">
                  <input
                    type="file"
                    id="smart-story-bible-upload"
                    accept=".pdf,.txt,.text"
                    multiple
                    className="hidden"
                    onChange={async (e) => {
                      const fileList = e.target.files;
                      if (!fileList || fileList.length === 0) return;
                      
                      // Clear and start activity log
                      setUploadActivityLog([]);
                      const logEntry = (msg) => {
                        // Ensure msg is a simple string to avoid serialization issues
                        const safeMsg = typeof msg === 'string' ? msg : String(msg);
                        setUploadActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: safeMsg }]);
                      };
                      
                      setUploadingStoryBible(true);
                      const formDataUpload = new FormData();
                      let totalSize = 0;
                      
                      for (let i = 0; i < fileList.length; i++) {
                        formDataUpload.append('files', fileList[i]);
                        totalSize += fileList[i].size;
                        logEntry(`Added file: "${fileList[i].name}" (${(fileList[i].size / 1024).toFixed(1)} KB)`);
                      }
                      
                      logEntry(`Starting upload of ${fileList.length} file(s) (${(totalSize / 1024).toFixed(1)} KB total)...`);
                      
                      try {
                        toast.loading("Parsing your story bible...", { id: "smart-upload" });
                        logEntry(`Extracting content (verbatim copy)...`);
                        
                        const res = await axios.post(`${API}/smart-upload`, formDataUpload, { timeout: 180000 });
                        toast.dismiss("smart-upload");
                        
                        logEntry(`Server response received`);
                        
                        if (res.data.success) {
                          const sb = res.data.story_bible;
                          logEntry(`SUCCESS: Found ${sb.characters?.length || 0} characters, ${sb.chapter_outlines?.length || 0} chapters`);
                          
                          if (res.data.has_conflicts) {
                            logEntry(`WARNING: ${res.data.conflicts.length} conflicts detected - please resolve`);
                          }
                          
                          setUploadedStoryBible(res.data.story_bible);
                          
                          // Check for conflicts
                          if (res.data.has_conflicts && res.data.conflicts?.length > 0) {
                            setUploadConflicts(res.data.conflicts);
                            toast.warning(`Found ${res.data.conflicts.length} conflicts to resolve`, { duration: 5000 });
                          } else {
                            setUploadConflicts([]);
                          }
                          
                          // Auto-fill form data from story bible (sb already declared above)
                          setFormData(prev => ({
                            ...prev,
                            title: sb.title || prev.title,
                            genre: sb.genre?.toLowerCase().replace(/\s+/g, '_') || prev.genre,
                            elevator_pitch: sb.elevator_pitch || prev.elevator_pitch,
                            setting: sb.world_building?.setting || prev.setting,
                            era: sb.world_building?.time_period || prev.era,
                            tone: sb.world_building?.tone || prev.tone,
                            num_chapters: sb.chapter_outlines?.length || prev.num_chapters
                          }));
                          
                          // Set characters
                          if (sb.characters?.length > 0) {
                            setCharacters(sb.characters.map((c, i) => ({
                              id: i + 1,
                              name: c.name || '',
                              role: c.role || 'supporting',
                              description: c.brief_description || c.description || '',
                              age: c.age || '',
                              gender: c.gender || '',
                              ethnicity: c.ethnicity || '',
                              height: c.height || '',
                              build: c.build || '',
                              hairColor: c.hair_color || '',
                              hairStyle: c.hair_style || '',
                              eyeColor: c.eye_color || '',
                              skinTone: c.skin_tone || '',
                              distinguishingFeatures: c.distinguishing_features || '',
                              personality: c.personality || '',
                              motivation: c.motivation || '',
                              backstory: c.backstory || ''
                            })));
                          }
                          
                          // Set chapter outlines if present
                          if (sb.chapter_outlines?.length > 0) {
                            setPlanReviewOutline(sb.chapter_outlines.map((ch, i) => ({
                              number: ch.number || i + 1,
                              title: ch.title || `Chapter ${ch.number || i + 1}`,
                              summary: ch.summary || '',
                              key_beats: ch.key_beats || [],
                              pov: ch.pov || '',
                              setting: ch.setting || ''
                            })));
                          }
                          
                          toast.success(res.data.message);
                          logEntry(`Upload complete!`);
                        } else {
                          logEntry(`ERROR: Server returned failure`);
                          toast.error("Failed to parse files");
                        }
                      } catch (err) {
                        toast.dismiss("smart-upload");
                        console.error("Smart upload error:", err);
                        const errorMsg = err.response?.data?.detail || err.message || "Failed to parse files";
                        logEntry(`ERROR: ${errorMsg}`);
                        toast.error(errorMsg);
                      } finally {
                        setUploadingStoryBible(false);
                        e.target.value = '';
                      }
                    }}
                    data-testid="smart-upload-file-input"
                  />
                  <label htmlFor="smart-story-bible-upload" className="cursor-pointer">
                    {uploadingStoryBible ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-12 h-12 animate-spin text-purple-400" />
                        <p className="text-sm text-muted-foreground">Analyzing your files...</p>
                        <p className="text-xs text-purple-400">Extracting characters, chapters, world-building...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-full bg-purple-600/20 flex items-center justify-center">
                          <FileUp className="w-8 h-8 text-purple-400" />
                        </div>
                        <p className="text-foreground font-medium">Click to upload files</p>
                        <p className="text-sm text-muted-foreground">PDF or TXT • Upload multiple files at once</p>
                      </div>
                    )}
                  </label>
                </div>
                
                {/* Activity Log */}
                {uploadActivityLog.length > 0 && (
                  <div className="bg-slate-900/80 border border-slate-700 rounded-lg p-3 max-h-40 overflow-y-auto" data-testid="smart-upload-activity-log-2">
                    <p className="text-xs font-mono text-slate-400 mb-1">Activity Log</p>
                    {uploadActivityLog.map((entry, i) => (
                      <p key={i} className={`text-xs font-mono ${entry.msg.startsWith('ERROR') ? 'text-red-400' : entry.msg.startsWith('SUCCESS') ? 'text-green-400' : entry.msg.startsWith('WARNING') ? 'text-yellow-400' : 'text-slate-400'}`}>
                        <span className="text-slate-500">[{entry.time}]</span> {entry.msg}
                      </p>
                    ))}
                  </div>
                )}
                
                <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-purple-300">What can you upload?</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• <strong>Character Profiles</strong> - Names, appearances, personalities, backstories</li>
                    <li>• <strong>Chapter Outlines</strong> - What happens in each chapter</li>
                    <li>• <strong>World-Building</strong> - Settings, rules, magic systems, technology</li>
                    <li>• <strong>Story Synopsis</strong> - Elevator pitch, beginning/middle/end</li>
                    <li>• <strong>All-in-One</strong> - A single document with everything</li>
                  </ul>
                  <p className="text-xs text-purple-400 mt-2">
                    <strong>Your words are sacred.</strong> We extract your content EXACTLY as written - no AI "improvements".
                  </p>
                </div>
              </div>
            )}
            
            {/* SMART UPLOAD RESULTS - Review what was extracted */}
            {smartUploadMode && uploadedStoryBible && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground text-lg flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-500" />
                      Story Bible Loaded!
                    </h3>
                    <p className="text-sm text-muted-foreground">Review what we extracted from your files</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setUploadedStoryBible(null); }}>
                    Upload Different Files
                  </Button>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-400">{uploadedStoryBible.characters?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Characters</p>
                  </div>
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-400">{uploadedStoryBible.chapter_outlines?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Chapters</p>
                  </div>
                </div>
                
                {/* Title & Genre */}
                {(uploadedStoryBible.title || uploadedStoryBible.genre) && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Story</p>
                    <p className="font-medium text-foreground">{uploadedStoryBible.title || 'Untitled'}</p>
                    {uploadedStoryBible.genre && <Badge variant="outline" className="mt-1">{uploadedStoryBible.genre}</Badge>}
                  </div>
                )}
                
                {/* Elevator Pitch */}
                {uploadedStoryBible.elevator_pitch && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Synopsis</p>
                    <p className="text-sm text-foreground">{uploadedStoryBible.elevator_pitch.slice(0, 200)}...</p>
                  </div>
                )}
                
                {/* Characters Preview */}
                {uploadedStoryBible.characters?.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-2">Characters ({uploadedStoryBible.characters.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {uploadedStoryBible.characters.slice(0, 8).map((c, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {c.name} <span className="text-muted-foreground ml-1">({c.role})</span>
                        </Badge>
                      ))}
                      {uploadedStoryBible.characters.length > 8 && (
                        <Badge variant="outline" className="text-xs">+{uploadedStoryBible.characters.length - 8} more</Badge>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Chapters Preview */}
                {uploadedStoryBible.chapter_outlines?.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-2">Chapter Outlines ({uploadedStoryBible.chapter_outlines.length})</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {uploadedStoryBible.chapter_outlines.slice(0, 5).map((ch, i) => (
                        <p key={i} className="text-xs text-foreground">
                          <span className="text-cyan-400 font-medium">Ch {ch?.number || i+1}:</span> {ch?.title || ch?.summary?.slice(0, 50) || 'Untitled'}
                        </p>
                      ))}
                      {uploadedStoryBible.chapter_outlines.length > 5 && (
                        <p className="text-xs text-muted-foreground">...and {uploadedStoryBible.chapter_outlines.length - 5} more chapters</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* CONFLICT RESOLUTION UI */}
                {uploadConflicts.length > 0 && (
                  <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-red-300">Conflicts Detected</p>
                        <p className="text-xs text-muted-foreground">We found some inconsistent information. Please choose which version is correct:</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {uploadConflicts.map((conflict, idx) => (
                        <div key={conflict.conflict_id || idx} className="bg-slate-800/50 rounded-lg p-3">
                          <p className="text-sm text-foreground mb-2">
                            <Badge variant="outline" className="mr-2 text-xs">{conflict.type}</Badge>
                            {conflict.description}
                          </p>
                          <div className="space-y-2">
                            {conflict.options?.map((opt) => (
                              <label 
                                key={opt.key} 
                                className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                                  conflictResolutions[conflict.conflict_id] === opt.key 
                                    ? opt.key === 'ignore' 
                                      ? 'bg-blue-900/30 border border-blue-500/50'
                                      : 'bg-green-900/30 border border-green-500/50' 
                                    : opt.key === 'ignore'
                                      ? 'bg-slate-800/30 border border-slate-600/30 hover:bg-slate-700/50'
                                      : 'hover:bg-slate-700/50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={conflict.conflict_id}
                                  value={opt.key}
                                  checked={conflictResolutions[conflict.conflict_id] === opt.key}
                                  onChange={() => setConflictResolutions(prev => ({
                                    ...prev,
                                    [conflict.conflict_id]: opt.key
                                  }))}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <p className={`text-sm font-medium ${opt.key === 'ignore' ? 'text-blue-300' : 'text-foreground'}`}>
                                    {opt.key === 'ignore' ? '⊘ ' : `${opt.key.toUpperCase()}. `}{opt.value}
                                  </p>
                                  {opt.source && opt.key !== 'ignore' && <p className="text-xs text-muted-foreground">Source: {opt.source}</p>}
                                </div>
                              </label>
                            ))}
                            {/* Always show ignore option if not present from backend */}
                            {!conflict.options?.some(o => o.key === 'ignore') && (
                              <label 
                                className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                                  conflictResolutions[conflict.conflict_id] === 'ignore' 
                                    ? 'bg-blue-900/30 border border-blue-500/50'
                                    : 'bg-slate-800/30 border border-slate-600/30 hover:bg-slate-700/50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={conflict.conflict_id}
                                  value="ignore"
                                  checked={conflictResolutions[conflict.conflict_id] === 'ignore'}
                                  onChange={() => setConflictResolutions(prev => ({
                                    ...prev,
                                    [conflict.conflict_id]: 'ignore'
                                  }))}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-blue-300">⊘ Ignore - Keep both (not a real conflict)</p>
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <Button
                      onClick={async () => {
                        // Check if all conflicts are resolved
                        const unresolvedCount = uploadConflicts.filter(c => !conflictResolutions[c.conflict_id]).length;
                        if (unresolvedCount > 0) {
                          toast.error(`Please resolve all ${unresolvedCount} remaining conflicts`);
                          return;
                        }
                        
                        setResolvingConflicts(true);
                        try {
                          toast.loading("Applying your choices...", { id: "resolve-conflicts" });
                          const res = await axios.post(`${API}/resolve-conflicts`, {
                            story_bible: uploadedStoryBible,
                            conflicts: uploadConflicts,
                            resolutions: Object.entries(conflictResolutions).map(([id, key]) => ({
                              conflict_id: id,
                              chosen_option: key
                            }))
                          });
                          toast.dismiss("resolve-conflicts");
                          
                          if (res.data.success) {
                            setUploadedStoryBible(res.data.story_bible);
                            setUploadConflicts([]);
                            setConflictResolutions({});
                            toast.success("Conflicts resolved! Your story bible is ready.");
                          } else {
                            toast.error("Failed to apply resolutions");
                          }
                        } catch (err) {
                          toast.dismiss("resolve-conflicts");
                          toast.error(err.response?.data?.detail || "Failed to resolve conflicts");
                        } finally {
                          setResolvingConflicts(false);
                        }
                      }}
                      disabled={resolvingConflicts || Object.keys(conflictResolutions).length < uploadConflicts.length}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="apply-conflict-choices-btn"
                    >
                      {resolvingConflicts ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Applying...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Apply My Choices ({Object.keys(conflictResolutions).length}/{uploadConflicts.length})
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex flex-col gap-2 pt-2">
                  {/* Primary Continue Button */}
                  <Button
                    onClick={() => {
                      // Check if we have minimum required data
                      const sb = uploadedStoryBible;
                      const hasTitle = sb?.title || formData.title;
                      const hasGenre = sb?.genre || formData.genre;
                      const hasChapters = planReviewOutline?.length > 0;
                      
                      // Clear conflicts since user is continuing
                      setUploadConflicts([]);
                      setConflictResolutions({});
                      
                      if (!hasTitle || !hasGenre) {
                        // Need to fill in basic info first - go to step 1
                        setComplexityLevel("detailed");
                        setStep(1);
                        setSmartUploadMode(false);
                        toast.info("Let's fill in the title and genre before creating your book.");
                      } else if (hasChapters) {
                        // Have chapters - go directly to chapter planning (last step)
                        setComplexityLevel("detailed");
                        setStep(7);
                        setChapterPlanMode('upload');
                        setSmartUploadMode(false);
                      } else {
                        // Have title/genre but no chapters - go to chapter planning to set up
                        setComplexityLevel("detailed");
                        setStep(7);
                        setSmartUploadMode(false);
                        toast.info("No chapter outlines found. You can create them in the next step.");
                      }
                    }}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    data-testid="smart-upload-continue"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Continue to Create Book
                  </Button>
                  
                  {/* Save Progress Button - allows skipping conflict resolution */}
                  {uploadConflicts.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        const sb = uploadedStoryBible;
                        const hasTitle = sb?.title || formData.title;
                        const hasGenre = sb?.genre || formData.genre;
                        
                        // Mark conflicts as skipped (user chose to keep all values)
                        setUploadedStoryBible(prev => ({
                          ...prev,
                          conflicts_skipped: true,
                          skipped_conflicts_count: uploadConflicts.length
                        }));
                        
                        // Clear conflicts
                        setUploadConflicts([]);
                        setConflictResolutions({});
                        
                        toast.success(`Saved! ${uploadConflicts.length} conflict(s) kept as-is.`);
                        
                        if (!hasTitle || !hasGenre) {
                          setComplexityLevel("detailed");
                          setStep(1);
                          setSmartUploadMode(false);
                          toast.info("Let's fill in the title and genre before creating your book.");
                        } else {
                          setComplexityLevel("detailed");
                          setStep(7);
                          setSmartUploadMode(false);
                        }
                      }}
                      className="flex-1 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                      data-testid="smart-upload-save-progress-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Progress (Skip Conflicts)
                    </Button>
                  )}
                </div>
                
                <p className="text-xs text-center text-muted-foreground">
                  You can edit any details in the next steps before generating
                </p>
              </div>
            )}

            {/* Progress indicator - dynamic based on complexity level */}
            {complexityLevel && !smartUploadMode && (
              <div className="flex gap-2 mb-4">
                {Array.from({ length: complexityLevel === 'simple' ? 2 : complexityLevel === 'moderate' ? 4 : 6 }, (_, i) => i + 1).map(s => (
                  <div 
                    key={s} 
                    className={`h-1 flex-1 rounded ${s <= step ? 'bg-cyan-600' : 'bg-slate-700'}`}
                  />
                ))}
              </div>
            )}

            {/* Step 1: Basic Info - only show when complexity is chosen */}
            {complexityLevel && step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Book Title *</Label>
                  <Input
                    id="title"
                    placeholder="The Adventures of..."
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    data-testid="new-book-title"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Book Type *</Label>
                    <Select value={formData.book_type} onValueChange={(v) => setFormData({ ...formData, book_type: v })}>
                      <SelectTrigger data-testid="book-type-select">
                        <SelectValue placeholder="Choose book type" />
                      </SelectTrigger>
                      <SelectContent>
                        {bookOptions?.book_types?.map(bt => (
                          <SelectItem key={bt.id} value={bt.id}>
                            <span className="font-medium">{bt.name}</span>
                          </SelectItem>
                        )) || (
                          <>
                            <SelectItem value="fiction">Fiction</SelectItem>
                            <SelectItem value="nonfiction">Non-Fiction</SelectItem>
                            <SelectItem value="self_help">Self-Help</SelectItem>
                            <SelectItem value="memoir">Memoir/Autobiography</SelectItem>
                            <SelectItem value="series">Series (Multi-Book)</SelectItem>
                            <SelectItem value="specialty">Specialty/Topic Book</SelectItem>
                            <SelectItem value="religious">Religious/Spiritual</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Genre *</Label>
                    <Select value={formData.genre} onValueChange={(v) => setFormData({ ...formData, genre: v })}>
                      <SelectTrigger data-testid="genre-select">
                        <SelectValue placeholder="Choose a genre" />
                      </SelectTrigger>
                      <SelectContent>
                        {genres.map(g => (
                          <SelectItem key={g.id} value={g.id}>
                            <span className="font-medium">{g.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Series Info - shown when book_type is "series" */}
                {formData.book_type === "series" && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                    <p className="text-sm font-medium text-blue-800">📚 Series Information</p>
                    <div>
                      <Label>Series Name</Label>
                      <Input
                        placeholder="e.g., The Chronicles of Narnia"
                        value={formData.series_name}
                        onChange={(e) => setFormData({ ...formData, series_name: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Total Books in Series</Label>
                        <Select value={String(formData.total_books)} onValueChange={(v) => setFormData({ ...formData, total_books: parseInt(v) })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[2,3,4,5,6,7,8,9,10,12,15,20].map(n => (
                              <SelectItem key={n} value={String(n)}>{n} books</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>This is Book #</Label>
                        <Select value={String(formData.book_number)} onValueChange={(v) => setFormData({ ...formData, book_number: parseInt(v) })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Array.from({length: formData.total_books}, (_, i) => i + 1).map(n => (
                              <SelectItem key={n} value={String(n)}>Book {n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Author Credentials - shown for non-fiction types */}
                {["nonfiction", "self_help", "memoir", "specialty", "academic", "religious"].includes(formData.book_type) && (
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-cyan-500/25 space-y-3">
                    <p className="text-sm font-medium text-cyan-200">✍️ Author Credentials</p>
                    <p className="text-xs text-cyan-400">Share qualifications that establish your authority on this topic</p>
                    <Textarea
                      placeholder="e.g., 20 years experience in..., PhD in..., Certified..., Author of..."
                      value={formData.author_credentials}
                      onChange={(e) => setFormData({ ...formData, author_credentials: e.target.value })}
                      rows={3}
                    />
                  </div>
                )}
                
                <div>
                  <Label>Target Length</Label>
                  <Select value={formData.target_length} onValueChange={(v) => setFormData({ ...formData, target_length: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="short_story">Short Story</SelectItem>
                      <SelectItem value="novella">Novella</SelectItem>
                      <SelectItem value="novel">Novel</SelectItem>
                      <SelectItem value="epic">Epic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Number of Chapters */}
                <div>
                  <Label>Number of Chapters</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.num_chapters}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setFormData({ ...formData, num_chapters: val === '' ? '' : parseInt(val) });
                    }}
                    onBlur={(e) => {
                      // Ensure valid value on blur
                      const val = parseInt(e.target.value);
                      if (!val || val < 1) setFormData({ ...formData, num_chapters: 10 });
                      else if (val > 200) setFormData({ ...formData, num_chapters: 200 });
                    }}
                    className="h-9"
                    placeholder="10"
                    data-testid="num-chapters-input"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Story Premise & Character - ALL LEVELS */}
            {step === 2 && bookOptions && (
              <div className="space-y-4">
                <div className="bg-slate-800/50 p-3 rounded-lg">
                  <h3 className="font-semibold text-cyan-200 text-sm">Story Premise & Characters</h3>
                  <p className="text-xs text-cyan-400">Define your characters with detailed physical appearance and personality</p>
                </div>
                
                {/* Character Editor - replaces basic protagonist inputs */}
                <CharacterEditor
                  characters={(formData.protagonists || []).map(p => ({
                    name: p.name || '',
                    role: p.role || 'protagonist',
                    description: p.description || '',
                    age: p.age || '',
                    gender: p.gender || '',
                    height: p.height || '',
                    build: p.build || '',
                    hairColor: p.hairColor || '',
                    hairStyle: p.hairStyle || '',
                    eyeColor: p.eyeColor || '',
                    skinTone: p.skinTone || '',
                    distinguishingFeatures: p.distinguishingFeatures || '',
                    personality: p.personality || '',
                    motivation: p.motivation || '',
                    backstory: p.backstory || '',
                  }))}
                  onChange={(characters) => setFormData({ ...formData, protagonists: characters })}
                  maxCharacters={30}
                  showTitle={true}
                />

                {/* Story Setting */}
                <div>
                  <Label className="text-sm font-medium">Story Setting</Label>
                  <Input
                    placeholder="e.g., Medieval Europe, Modern NYC, Space Colony"
                    value={formData.setting}
                    onChange={(e) => setFormData({ ...formData, setting: e.target.value })}
                  />
                </div>
                
                {/* Quick dropdowns for Simple mode */}
                {complexityLevel === 'simple' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Audience</Label>
                      <Select value={formData.audience_age} onValueChange={(v) => setFormData({ ...formData, audience_age: v })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {bookOptions.audience_ages?.map(opt => (
                            <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Tone</Label>
                      <Select value={formData.tone} onValueChange={(v) => setFormData({ ...formData, tone: v })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {bookOptions.tones?.map(opt => (
                            <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                
                {/* Elevator Pitch for Standard & Full */}
                {(complexityLevel === 'moderate' || complexityLevel === 'detailed') && (
                  <div className="relative">
                    <Label className="text-xs">Elevator Pitch (1-2 sentences)</Label>
                    <Textarea
                      placeholder="In one or two sentences, what is your story about? (e.g., 'A disgraced knight must protect the child heir to the throne from assassins while uncovering a conspiracy that reaches the highest levels of the kingdom.')"
                      value={formData.elevator_pitch}
                      onChange={(e) => setFormData({ ...formData, elevator_pitch: e.target.value })}
                      className="min-h-[60px] pr-10"
                    />
                    <SpeechToTextButton
                      onTranscript={(text, append) => setFormData(prev => ({ 
                        ...prev, 
                        elevator_pitch: append && prev.elevator_pitch ? prev.elevator_pitch + ' ' + text : text 
                      }))}
                      className="absolute right-2 top-7 h-6 w-6"
                      size="icon"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Story Premise (Beginning/Middle/End) - STANDARD & FULL */}
            {step === 3 && (complexityLevel === 'moderate' || complexityLevel === 'detailed') && (
              <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h3 className="font-semibold text-blue-800 text-sm">Story Premise</h3>
                  <p className="text-xs text-blue-600">Outline the three acts of your story</p>
                </div>
                
                {/* Beginning */}
                <div className="relative">
                  <Label className="text-xs text-green-700 flex items-center gap-1">
                    <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-xs">1</span>
                    Beginning - The Setup
                  </Label>
                  <Textarea
                    placeholder="What does the world look like before the main conflict? Introduce your characters' normal life before everything changes..."
                    value={formData.premise_beginning}
                    onChange={(e) => setFormData({ ...formData, premise_beginning: e.target.value })}
                    className="min-h-[70px] pr-10 mt-1"
                  />
                  <SpeechToTextButton
                    onTranscript={(text, append) => setFormData(prev => ({ 
                      ...prev, 
                      premise_beginning: append && prev.premise_beginning ? prev.premise_beginning + ' ' + text : text 
                    }))}
                    className="absolute right-2 top-7 h-6 w-6"
                    size="icon"
                  />
                </div>
                
                {/* Middle */}
                <div className="relative">
                  <Label className="text-xs text-cyan-300 flex items-center gap-1">
                    <span className="w-5 h-5 rounded-full bg-slate-800/60 flex items-center justify-center text-xs">2</span>
                    Middle - The Conflict
                  </Label>
                  <Textarea
                    placeholder="The rising conflict and major turning points. What challenges does your protagonist face? What is the midpoint twist?"
                    value={formData.premise_middle}
                    onChange={(e) => setFormData({ ...formData, premise_middle: e.target.value })}
                    className="min-h-[70px] pr-10 mt-1"
                  />
                  <SpeechToTextButton
                    onTranscript={(text, append) => setFormData(prev => ({ 
                      ...prev, 
                      premise_middle: append && prev.premise_middle ? prev.premise_middle + ' ' + text : text 
                    }))}
                    className="absolute right-2 top-7 h-6 w-6"
                    size="icon"
                  />
                </div>
                
                {/* Ending */}
                <div className="relative">
                  <Label className="text-xs text-purple-700 flex items-center gap-1">
                    <span className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-xs">3</span>
                    Ending - The Resolution
                  </Label>
                  <Textarea
                    placeholder="How does the story resolve? How have the characters changed? What is the climax and aftermath?"
                    value={formData.premise_ending}
                    onChange={(e) => setFormData({ ...formData, premise_ending: e.target.value })}
                    className="min-h-[70px] pr-10 mt-1"
                  />
                  <SpeechToTextButton
                    onTranscript={(text, append) => setFormData(prev => ({ 
                      ...prev, 
                      premise_ending: append && prev.premise_ending ? prev.premise_ending + ' ' + text : text 
                    }))}
                    className="absolute right-2 top-7 h-6 w-6"
                    size="icon"
                  />
                </div>
              </div>
            )}

            {/* Step 3 for Simple / Step 4 for Standard & Full: Advanced Options */}
            {((complexityLevel === 'moderate' || complexityLevel === 'detailed') && step === 4) && bookOptions && (
              <div className="space-y-4">
                <div className="bg-background p-3 rounded-lg">
                  <h3 className="font-semibold text-foreground text-sm">Story Details</h3>
                  <p className="text-xs text-muted-foreground">Fine-tune your story's settings</p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Audience</Label>
                    <Select value={formData.audience_age} onValueChange={(v) => setFormData({ ...formData, audience_age: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {bookOptions.audience_ages?.map(opt => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Content Rating</Label>
                    <Select value={formData.content_rating} onValueChange={(v) => setFormData({ ...formData, content_rating: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {bookOptions.content_ratings?.map(opt => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Specialty</Label>
                    <Select value={formData.specialty_subject} onValueChange={(v) => setFormData({ ...formData, specialty_subject: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {bookOptions.specialty_subjects?.map(opt => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Tone</Label>
                    <Select value={formData.tone} onValueChange={(v) => setFormData({ ...formData, tone: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {bookOptions.tones?.map(opt => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Antagonist - Standard & Full */}
                {(complexityLevel === 'moderate' || complexityLevel === 'detailed') && (
                  <>
                    <div className="border-t pt-3 mt-3">
                      <Label className="text-xs text-red-700">Antagonist (Optional)</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Antagonist Name"
                        value={formData.antagonist_name}
                        onChange={(e) => setFormData({ ...formData, antagonist_name: e.target.value })}
                        className="h-9"
                      />
                      <Select value={formData.antagonist_type} onValueChange={(v) => setFormData({ ...formData, antagonist_type: v })}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="human">Human</SelectItem>
                          <SelectItem value="creature">Creature/Monster</SelectItem>
                          <SelectItem value="supernatural">Supernatural Force</SelectItem>
                          <SelectItem value="nature">Nature/Environment</SelectItem>
                          <SelectItem value="society">Society/System</SelectItem>
                          <SelectItem value="self">Internal (Self)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea
                      placeholder="Describe your antagonist..."
                      value={formData.antagonist_description}
                      onChange={(e) => setFormData({ ...formData, antagonist_description: e.target.value })}
                      className="min-h-[50px]"
                    />
                  </>
                )}
              </div>
            )}

            {/* Step 5: World-Building - FULL MODE ONLY */}
            {complexityLevel === 'detailed' && step === 5 && (
              <div className="space-y-4">
                <div className="bg-indigo-50 p-3 rounded-lg">
                  <h3 className="font-semibold text-indigo-800 text-sm flex items-center gap-2">
                    <Globe className="w-4 h-4" /> World-Building
                  </h3>
                  <p className="text-xs text-indigo-600">Define the rules and nature of your story world</p>
                </div>
                
                {/* World Type & Era */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">World Type</Label>
                    <Select value={formData.world_type} onValueChange={(v) => setFormData({ ...formData, world_type: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="earth">Real Earth</SelectItem>
                        <SelectItem value="alternate_earth">Alternate Earth</SelectItem>
                        <SelectItem value="fantasy">Fantasy World</SelectItem>
                        <SelectItem value="scifi">Sci-Fi Universe</SelectItem>
                        <SelectItem value="other">Other/Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Era/Time Period</Label>
                    <Select value={formData.era} onValueChange={(v) => setFormData({ ...formData, era: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ancient">Ancient (Before 500 AD)</SelectItem>
                        <SelectItem value="medieval">Medieval (500-1500)</SelectItem>
                        <SelectItem value="renaissance">Renaissance (1400-1700)</SelectItem>
                        <SelectItem value="victorian">Victorian (1800s)</SelectItem>
                        <SelectItem value="modern">Modern (1900-2000)</SelectItem>
                        <SelectItem value="contemporary">Contemporary (Present)</SelectItem>
                        <SelectItem value="near_future">Near Future</SelectItem>
                        <SelectItem value="far_future">Far Future</SelectItem>
                        <SelectItem value="custom">Custom/Timeless</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {formData.world_type === 'other' || formData.world_type === 'fantasy' ? (
                  <Input
                    placeholder="Name your world (e.g., Westeros, Middle-earth)"
                    value={formData.world_name}
                    onChange={(e) => setFormData({ ...formData, world_name: e.target.value })}
                  />
                ) : null}
                
                {/* Technology Level */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Technology Level</Label>
                    <Select value={formData.tech_level} onValueChange={(v) => setFormData({ ...formData, tech_level: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Technology</SelectItem>
                        <SelectItem value="primitive">Primitive (Stone Age)</SelectItem>
                        <SelectItem value="medieval">Medieval (Swords, Horses)</SelectItem>
                        <SelectItem value="industrial">Industrial (Steam, Early Machines)</SelectItem>
                        <SelectItem value="modern">Modern (Computers, Internet)</SelectItem>
                        <SelectItem value="advanced">Advanced (AI, Space Travel)</SelectItem>
                        <SelectItem value="scifi">Sci-Fi (FTL, Alien Tech)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Tech Importance in Story</Label>
                    <Select value={formData.tech_importance} onValueChange={(v) => setFormData({ ...formData, tech_importance: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Relevant</SelectItem>
                        <SelectItem value="background">Background Element</SelectItem>
                        <SelectItem value="moderate">Moderately Important</SelectItem>
                        <SelectItem value="central">Central to Plot</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Society Structure */}
                <div>
                  <Label className="text-xs">Society Structure</Label>
                  <Select value={formData.society_structure} onValueChange={(v) => setFormData({ ...formData, society_structure: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tribal">Tribal/Clan-based</SelectItem>
                      <SelectItem value="feudal">Feudal (Lords & Peasants)</SelectItem>
                      <SelectItem value="monarchy">Monarchy/Empire</SelectItem>
                      <SelectItem value="democratic">Democratic/Republic</SelectItem>
                      <SelectItem value="authoritarian">Authoritarian/Dystopian</SelectItem>
                      <SelectItem value="anarchic">Anarchic/Lawless</SelectItem>
                      <SelectItem value="corporate">Corporate/Cyberpunk</SelectItem>
                      <SelectItem value="other">Other/Mixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 6: Supernatural & Religion - FULL MODE ONLY */}
            {complexityLevel === 'detailed' && step === 6 && (
              <div className="space-y-4">
                <div className="bg-purple-50 p-3 rounded-lg">
                  <h3 className="font-semibold text-purple-800 text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Magic, Supernatural & Religion
                  </h3>
                  <p className="text-xs text-purple-600">Define the mystical elements of your world</p>
                </div>
                
                {/* Supernatural */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Does your world have supernatural elements?</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={formData.has_supernatural ? "default" : "outline"}
                        onClick={() => setFormData({ ...formData, has_supernatural: true })}
                        className="h-7"
                      >
                        Yes
                      </Button>
                      <Button
                        size="sm"
                        variant={!formData.has_supernatural ? "default" : "outline"}
                        onClick={() => setFormData({ ...formData, has_supernatural: false })}
                        className="h-7"
                      >
                        No
                      </Button>
                    </div>
                  </div>
                  
                  {formData.has_supernatural && (
                    <>
                      <div>
                        <Label className="text-xs">Types of Supernatural (select all that apply)</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {['Magic', 'Demons', 'Angels', 'Psychic Powers', 'Ghosts/Spirits', 'Gods/Deities', 'Vampires', 'Werewolves', 'Other'].map(type => (
                            <Badge 
                              key={type}
                              variant={formData.supernatural_type?.includes(type.toLowerCase()) ? "default" : "outline"}
                              className="cursor-pointer hover:bg-purple-100"
                              onClick={() => {
                                const current = formData.supernatural_type || [];
                                const typeKey = type.toLowerCase();
                                setFormData({
                                  ...formData,
                                  supernatural_type: current.includes(typeKey) 
                                    ? current.filter(t => t !== typeKey)
                                    : [...current, typeKey]
                                });
                              }}
                            >
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <Textarea
                        placeholder="Describe the rules of magic/supernatural in your world (e.g., 'Magic requires a sacrifice', 'Only certain bloodlines can use it')"
                        value={formData.supernatural_rules}
                        onChange={(e) => setFormData({ ...formData, supernatural_rules: e.target.value })}
                        className="min-h-[60px]"
                      />
                    </>
                  )}
                </div>
                
                {/* Religion */}
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Is religion important in your story?</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={formData.has_religion ? "default" : "outline"}
                        onClick={() => setFormData({ ...formData, has_religion: true })}
                        className="h-7"
                      >
                        Yes
                      </Button>
                      <Button
                        size="sm"
                        variant={!formData.has_religion ? "default" : "outline"}
                        onClick={() => setFormData({ ...formData, has_religion: false })}
                        className="h-7"
                      >
                        No
                      </Button>
                    </div>
                  </div>
                  
                  {formData.has_religion && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Religion Type</Label>
                          <Select value={formData.religion_type} onValueChange={(v) => setFormData({ ...formData, religion_type: v })}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mono">Monotheistic (One God)</SelectItem>
                              <SelectItem value="poly">Polytheistic (Many Gods)</SelectItem>
                              <SelectItem value="spiritual">Spiritual/Animistic</SelectItem>
                              <SelectItem value="ancestral">Ancestor Worship</SelectItem>
                              <SelectItem value="custom">Custom/Fictional</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Importance in Story</Label>
                          <Select value={formData.religion_importance} onValueChange={(v) => setFormData({ ...formData, religion_importance: v })}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="background">Background Element</SelectItem>
                              <SelectItem value="moderate">Moderately Important</SelectItem>
                              <SelectItem value="central">Central to Plot</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Textarea
                        placeholder="Describe the religion(s) in your world..."
                        value={formData.religion_description}
                        onChange={(e) => setFormData({ ...formData, religion_description: e.target.value })}
                        className="min-h-[50px]"
                      />
                    </>
                  )}
                </div>
                
                {/* Central Theme */}
                <div className="border-t pt-4">
                  <Label className="text-xs">Central Theme/Message (Optional)</Label>
                  <Input
                    placeholder="e.g., 'Love conquers all', 'Power corrupts', 'Family is everything'"
                    value={formData.theme}
                    onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* CHAPTER PLANNING STEP - Final step for all complexity levels */}
            {((complexityLevel === 'simple' && step === 3) || (complexityLevel === 'moderate' && step === 5) || (complexityLevel === 'detailed' && step === 7)) && (
              <div className="space-y-4">
                {!chapterPlanMode ? (
                  <>
                    <div className="bg-background p-3 rounded-lg">
                      <h3 className="font-semibold text-foreground text-sm">Chapter Planning</h3>
                      <p className="text-xs text-muted-foreground">How do you want to plan your {formData.num_chapters} chapters?</p>
                    </div>
                    
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          setChapterPlanMode('manual');
                          setManualChapters(Array.from({ length: formData.num_chapters }, (_, i) => ({ number: i + 1, title: '', description: '' })));
                        }}
                        className="w-full text-left p-4 rounded-lg border border-zinc-700 hover:border-cyan-500 hover:bg-cyan-500/5 transition-all"
                        data-testid="plan-mode-manual"
                      >
                        <div className="font-semibold text-foreground text-sm">I'll Write Each Chapter</div>
                        <p className="text-xs text-muted-foreground mt-1">Fill in what happens in each chapter. Leave any blank and AI will fill them in for you.</p>
                      </button>
                      
                      <button
                        onClick={() => {
                          setChapterPlanMode('situations');
                          setUserSituations([{ description: '' }]);
                        }}
                        className="w-full text-left p-4 rounded-lg border border-zinc-700 hover:border-cyan-500 hover:bg-cyan-500/5 transition-all"
                        data-testid="plan-mode-situations"
                      >
                        <div className="font-semibold text-foreground text-sm">I Have Key Moments</div>
                        <p className="text-xs text-muted-foreground mt-1">Tell us the key situations, scenes, or events you want. AI will distribute them across chapters and fill in the rest.</p>
                      </button>
                      
                      <button
                        onClick={() => setChapterPlanMode('ai')}
                        className="w-full text-left p-4 rounded-lg border border-zinc-700 hover:border-cyan-500 hover:bg-cyan-500/5 transition-all"
                        data-testid="plan-mode-ai"
                      >
                        <div className="font-semibold text-foreground text-sm">Let AI Plan Everything</div>
                        <p className="text-xs text-muted-foreground mt-1">AI will create a full chapter-by-chapter plan based on your characters, world, and story details.</p>
                      </button>
                      
                      <button
                        onClick={() => setChapterPlanMode('upload')}
                        className="w-full text-left p-4 rounded-lg border border-purple-700 hover:border-purple-500 hover:bg-purple-500/10 transition-all bg-purple-900/20"
                        data-testid="plan-mode-upload"
                      >
                        <div className="font-semibold text-foreground text-sm flex items-center gap-2">
                          <Upload className="w-4 h-4 text-purple-400" />
                          Upload My Chapter Outline
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Already have a chapter outline? Upload a PDF or TXT file. Your chapters will be used EXACTLY as written.</p>
                      </button>
                    </div>
                    
                    <div className="mt-2">
                      <Label className="text-xs">Number of Chapters</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.num_chapters}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          const n = val === '' ? '' : parseInt(val);
                          setFormData({ ...formData, num_chapters: n });
                        }}
                        className="w-32 h-9 mt-1"
                        data-testid="chapter-count-input"
                      />
                    </div>
                  </>
                ) : chapterPlanMode === 'manual' && !planReviewOutline ? (
                  <>
                    <div className="bg-background p-3 rounded-lg flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">Write Your Chapter Plan</h3>
                        <p className="text-xs text-muted-foreground">Describe what happens in each chapter. Leave blank for AI to fill in.</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setChapterPlanMode(null)} data-testid="plan-change-mode">Change</Button>
                    </div>
                    
                    <div className="max-h-[400px] overflow-y-auto space-y-3 pr-1">
                      {manualChapters.map((ch, i) => (
                        <div key={i} className="p-3 rounded-lg border border-zinc-800 bg-zinc-900/50">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-cyan-400 w-6">#{ch.number}</span>
                            <Input
                              placeholder={`Chapter ${ch.number} title (optional)`}
                              value={ch.title}
                              onChange={(e) => {
                                const updated = [...manualChapters];
                                updated[i] = { ...updated[i], title: e.target.value };
                                setManualChapters(updated);
                              }}
                              className="h-8 text-sm flex-1"
                              data-testid={`manual-chapter-title-${ch.number}`}
                            />
                          </div>
                          <Textarea
                            placeholder={`What happens in chapter ${ch.number}? (Leave blank for AI to plan)`}
                            value={ch.description}
                            onChange={(e) => {
                              const updated = [...manualChapters];
                              updated[i] = { ...updated[i], description: e.target.value };
                              setManualChapters(updated);
                            }}
                            className="min-h-[60px] text-sm"
                            data-testid={`manual-chapter-desc-${ch.number}`}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                ) : chapterPlanMode === 'situations' && !planReviewOutline ? (
                  <>
                    <div className="bg-background p-3 rounded-lg flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">Your Key Moments</h3>
                        <p className="text-xs text-muted-foreground">Add the scenes, events, or situations you want in your story. AI will weave them into {formData.num_chapters} chapters.</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setChapterPlanMode(null)} data-testid="plan-change-mode-sit">Change</Button>
                    </div>
                    
                    <div className="max-h-[400px] overflow-y-auto space-y-3 pr-1">
                      {userSituations.map((sit, i) => (
                        <div key={i} className="flex gap-2 items-start">
                          <span className="text-xs font-bold text-cyan-400 mt-3 w-6">{i + 1}</span>
                          <Textarea
                            placeholder={`Describe a key moment, scene, or event...`}
                            value={sit.description}
                            onChange={(e) => {
                              const updated = [...userSituations];
                              updated[i] = { description: e.target.value };
                              setUserSituations(updated);
                            }}
                            className="min-h-[60px] text-sm flex-1"
                            data-testid={`situation-input-${i}`}
                          />
                          {userSituations.length > 1 && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => setUserSituations(userSituations.filter((_, j) => j !== i))}
                              className="mt-2 text-red-400 hover:text-red-300"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setUserSituations([...userSituations, { description: '' }])}
                      className="w-full border-dashed"
                      data-testid="add-situation-btn"
                    >
                      + Add Another Moment
                    </Button>
                  </>
                ) : chapterPlanMode === 'ai' && !planReviewOutline ? (
                  <>
                    <div className="bg-background p-3 rounded-lg flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">AI-Generated Plan</h3>
                        <p className="text-xs text-muted-foreground">AI will create a complete {formData.num_chapters}-chapter plan from your story details.</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setChapterPlanMode(null)} data-testid="plan-change-mode-ai">Change</Button>
                    </div>
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="text-sm">Click <strong>"Generate Plan"</strong> below and AI will create your chapter outline based on everything you've told us about your characters, world, and story.</p>
                    </div>
                  </>
                ) : chapterPlanMode === 'upload' && !planReviewOutline ? (
                  <>
                    <div className="bg-background p-3 rounded-lg flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
                          <Upload className="w-4 h-4 text-purple-400" />
                          Upload Your Chapter Outline
                        </h3>
                        <p className="text-xs text-muted-foreground">Upload a PDF or TXT file with your chapters. We'll use them EXACTLY as you wrote them.</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setChapterPlanMode(null)} data-testid="plan-change-mode-upload">Change</Button>
                    </div>
                    
                    <div className="border-2 border-dashed border-purple-500/50 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                      <input
                        type="file"
                        id="chapter-outline-upload"
                        accept=".pdf,.txt,.text"
                        multiple
                        className="hidden"
                        onChange={async (e) => {
                          const fileList = e.target.files;
                          if (!fileList || fileList.length === 0) return;
                          
                          // Activity log
                          setUploadActivityLog([]);
                          const logEntry = (msg) => {
                        // Ensure msg is a simple string to avoid serialization issues
                        const safeMsg = typeof msg === 'string' ? msg : String(msg);
                        setUploadActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: safeMsg }]);
                      };
                          
                          setUploadingOutline(true);
                          const formDataUpload = new FormData();
                          let totalSize = 0;
                          
                          // Support multiple files
                          for (let i = 0; i < fileList.length; i++) {
                            formDataUpload.append('files', fileList[i]);
                            totalSize += fileList[i].size;
                            logEntry(`Added file: "${fileList[i].name}" (${(fileList[i].size / 1024).toFixed(1)} KB)`);
                          }
                          
                          logEntry(`Starting upload of ${fileList.length} file(s)...`);
                          
                          try {
                            toast.loading(`Parsing ${fileList.length} file(s)...`, { id: "outline-upload" });
                            logEntry(`Extracting content (verbatim copy)...`);
                            
                            const res = await axios.post(`${API}/chapter-outlines/parse`, formDataUpload, { timeout: 180000 });
                            toast.dismiss("outline-upload");
                            
                            logEntry(`Server response received`);
                            
                            if (res.data.success && res.data.chapters?.length > 0) {
                              // Convert to planReviewOutline format
                              const chapters = res.data.chapters.map((ch, i) => ({
                                number: ch.number || i + 1,
                                title: ch.title || `Chapter ${ch.number || i + 1}`,
                                summary: ch.summary || '',
                                key_beats: ch.key_beats || [],
                                pov: ch.pov || '',
                                setting: ch.setting || '',
                                characters_present: ch.characters_present || []
                              }));
                              
                              logEntry(`SUCCESS: Found ${chapters.length} chapters`);
                              chapters.slice(0, 5).forEach((ch, i) => {
                                logEntry(`  Chapter ${ch.number}: ${ch.title}`);
                              });
                              if (chapters.length > 5) {
                                logEntry(`  ... and ${chapters.length - 5} more chapters`);
                              }
                              
                              // Update form data with chapter count
                              setFormData(prev => ({ ...prev, num_chapters: chapters.length }));
                              setPlanReviewOutline(chapters);
                              toast.success(`Loaded ${chapters.length} chapters from ${fileList.length} file(s)!`);
                              logEntry(`Upload complete!`);
                            } else {
                              logEntry(`ERROR: No chapters found in the file(s)`);
                              toast.error("No chapters found in the file(s). Please check the format.");
                            }
                          } catch (err) {
                            toast.dismiss("outline-upload");
                            console.error("Chapter outline upload error:", err);
                            
                            // Better error messages
                            let errorMsg = "Failed to parse chapter outlines";
                            if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
                              errorMsg = "Upload timed out - try smaller files";
                              logEntry(`ERROR: Request timed out after 3 minutes`);
                            } else if (err.message === 'Network Error') {
                              errorMsg = "Network error - check your connection";
                              logEntry(`ERROR: Network error (server may be unavailable)`);
                            } else if (err.response?.status === 404) {
                              errorMsg = "Endpoint not found - please refresh and try again";
                              logEntry(`ERROR: 404 Not Found`);
                            } else if (err.response?.data?.detail) {
                              errorMsg = err.response.data.detail;
                              logEntry(`ERROR: ${errorMsg}`);
                            } else {
                              logEntry(`ERROR: ${err.message || errorMsg}`);
                            }
                            
                            toast.error(errorMsg);
                          } finally {
                            setUploadingOutline(false);
                            e.target.value = ''; // Reset file input
                          }
                        }}
                        data-testid="chapter-outline-file-input"
                      />
                      <label htmlFor="chapter-outline-upload" className="cursor-pointer">
                        {uploadingOutline ? (
                          <div className="flex flex-col items-center gap-3">
                            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                            <p className="text-sm text-muted-foreground">Parsing your chapters...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-3">
                            <FileUp className="w-8 h-8 text-purple-400" />
                            <p className="text-sm text-foreground font-medium">Click to upload your chapter outline(s)</p>
                            <p className="text-xs text-muted-foreground">PDF or TXT — Upload multiple files at once</p>
                          </div>
                        )}
                      </label>
                    </div>
                    
                    {/* Activity Log for Chapter Outline Upload */}
                    {uploadActivityLog.length > 0 && (
                      <div className="bg-slate-900/80 border border-slate-700 rounded-lg p-3 max-h-40 overflow-y-auto mt-3" data-testid="chapter-upload-activity-log">
                        <p className="text-xs font-mono text-slate-400 mb-1">Activity Log</p>
                        {uploadActivityLog.map((entry, i) => (
                          <p key={i} className={`text-xs font-mono ${entry.msg.startsWith('ERROR') ? 'text-red-400' : entry.msg.startsWith('SUCCESS') ? 'text-green-400' : entry.msg.startsWith('WARNING') ? 'text-yellow-400' : 'text-slate-400'}`}>
                            <span className="text-slate-500">[{entry.time}]</span> {entry.msg}
                          </p>
                        ))}
                      </div>
                    )}
                    
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3 mt-2">
                      <p className="text-xs text-purple-300">
                        <strong>Your words are sacred.</strong> We will extract your chapters EXACTLY as written. 
                        No changes, no "improvements", no AI rewrites. Your outline becomes the blueprint.
                      </p>
                    </div>
                  </>
                ) : planReviewOutline ? (
                  <>
                    <div className="bg-background p-3 rounded-lg flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">Review Your Chapter Plan</h3>
                        <p className="text-xs text-muted-foreground">Edit any chapter before creating your book. Click "Create Book" when ready.</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setPlanReviewOutline(null)} data-testid="plan-edit-back">Re-edit</Button>
                    </div>
                    
                    {/* Chapter Size Selector for Manual Mode */}
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                      <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                        <BookOpen className="w-4 h-4" />
                        Chapter Length
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => setChapterSize("auto")}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            chapterSize === "auto"
                              ? "border-emerald-500 bg-emerald-500/10"
                              : "border-slate-600 hover:border-slate-500"
                          }`}
                          data-testid="manual-chapter-size-auto"
                        >
                          <p className="text-sm font-medium text-foreground">Auto ✨</p>
                          <p className="text-xs text-muted-foreground">Fits each chapter to its outline</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setChapterSize("medium")}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            chapterSize === "medium" 
                              ? "border-cyan-500 bg-cyan-500/10" 
                              : "border-slate-600 hover:border-slate-500"
                          }`}
                          data-testid="manual-chapter-size-medium"
                        >
                          <p className="text-sm font-medium text-foreground">Medium</p>
                          <p className="text-xs text-muted-foreground">800 - 1,000 words/chapter</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setChapterSize("large")}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            chapterSize === "large" 
                              ? "border-purple-500 bg-purple-500/10" 
                              : "border-slate-600 hover:border-slate-500"
                          }`}
                          data-testid="manual-chapter-size-large"
                        >
                          <p className="text-sm font-medium text-foreground">Large</p>
                          <p className="text-xs text-muted-foreground">1,800 - 2,100 words/chapter</p>
                        </button>
                      </div>
                    </div>
                    
                    <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
                      {planReviewOutline.map((ch, i) => (
                        <div key={i} className="p-3 rounded-lg border border-zinc-800 bg-zinc-900/50">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-cyan-400">Ch {ch.number}</span>
                            <Input
                              value={ch.title}
                              onChange={(e) => {
                                const updated = [...planReviewOutline];
                                updated[i] = { ...updated[i], title: e.target.value };
                                setPlanReviewOutline(updated);
                              }}
                              className="h-7 text-sm flex-1 font-semibold"
                            />
                          </div>
                          <Textarea
                            value={ch.summary}
                            onChange={(e) => {
                              const updated = [...planReviewOutline];
                              updated[i] = { ...updated[i], summary: e.target.value };
                              setPlanReviewOutline(updated);
                            }}
                            className="min-h-[50px] text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            )}

            <DialogFooter className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => step === 1 ? (complexityLevel ? setComplexityLevel(null) : setMode("choose")) : setStep(step - 1)}>
                Back
              </Button>
              {formData.title && (
                <Button 
                  variant="outline"
                  className="border-cyan-500/30 text-cyan-300 hover:bg-slate-800/50"
                  onClick={() => {
                    try {
                      const _uid = JSON.parse(localStorage.getItem('shweebum_user') || '{}').id || '';
                      const _draftStorageKey = _uid ? `shweebum_story_drafts_${_uid}` : 'shweebum_story_drafts';
                      const drafts = JSON.parse(localStorage.getItem(_draftStorageKey) || '{}');
                      const draftKey = `manual_${formData.title.toLowerCase().trim()}`;
                      drafts[draftKey] = {
                        mode: "manual",
                        formData,
                        complexityLevel,
                        savedAt: new Date().toISOString(),
                        title: formData.title,
                        step
                      };
                      localStorage.setItem(_draftStorageKey, JSON.stringify(drafts));
                      setHasUnsavedChanges(false);
                      toast.success(`Draft saved! "${formData.title}" - Come back anytime.`);
                    } catch (err) {
                      toast.error("Failed to save draft");
                    }
                  }}
                  data-testid="save-draft-manual-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save Draft
                </Button>
              )}
              {(() => {
                // Determine max steps based on complexity
                const maxSteps = complexityLevel === 'simple' ? 3 : complexityLevel === 'moderate' ? 5 : 7;
                const isLastStep = step >= maxSteps;
                const isPlanningStep = isLastStep;
                
                if (!isLastStep) {
                  return (
                    <Button 
                      onClick={() => setStep(step + 1)}
                      className="bg-cyan-700 hover:bg-cyan-800"
                      disabled={step === 1 && (!formData.title || !formData.genre)}
                    >
                      Next
                    </Button>
                  );
                } else if (isPlanningStep && planReviewOutline) {
                  // Review is showing - Create the book
                  return (
                    <Button 
                      onClick={handleManualCreate} 
                      className="bg-cyan-700 hover:bg-cyan-800"
                      disabled={loading}
                      data-testid="create-book-final-btn"
                    >
                      {loading ? "Creating..." : "Create Book"}
                    </Button>
                  );
                } else if (isPlanningStep && chapterPlanMode === 'manual') {
                  // Manual mode - confirm plan
                  return (
                    <Button 
                      onClick={() => {
                        const outline = manualChapters.map(ch => ({
                          number: ch.number,
                          title: ch.title || `Chapter ${ch.number}`,
                          summary: ch.description || '',
                          user_provided: !!ch.description
                        }));
                        setPlanReviewOutline(outline);
                      }}
                      className="bg-cyan-700 hover:bg-cyan-800"
                      data-testid="confirm-plan-btn"
                    >
                      Confirm Plan
                    </Button>
                  );
                } else if (isPlanningStep && (chapterPlanMode === 'situations' || chapterPlanMode === 'ai')) {
                  // Situations or AI mode - generate plan
                  return (
                    <Button 
                      onClick={handleGenerateChapterPlan}
                      className="bg-cyan-700 hover:bg-cyan-800"
                      disabled={generatingPlan || (chapterPlanMode === 'situations' && !userSituations.some(s => s.description.trim()))}
                      data-testid="generate-plan-btn"
                    >
                      {generatingPlan ? "Generating Plan..." : "Generate Plan"}
                    </Button>
                  );
                } else {
                  // No mode selected yet on planning step
                  return null;
                }
              })()}
            </DialogFooter>
          </>
  );
};

export default ManualBuilderMode;
